import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Search, Filter, Star, MapPin, Clock, Shield, AlertTriangle,
  ShoppingBag, Baby, Shirt, GraduationCap, Car, PartyPopper,
  Camera, Tent, TreePine, Building, Gamepad2, BookOpen, Gift,
  Heart, ChevronRight, Calendar, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { BabyKidsTrustBadge } from '../../components/services/BabyKidsBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subcategory icons mapping
const SUBCATEGORY_ICONS = {
  baby_gear_furniture: Baby,
  toys_games: Gamepad2,
  baby_clothing: Shirt,
  kids_clothing: Shirt,
  school_supplies: GraduationCap,
  prams_strollers: ShoppingBag,
  car_seats: Car,
  party_entertainers: PartyPopper,
  party_photographers: Camera,
  party_setup: PartyPopper,
  outdoor_party: TreePine,
  party_venues: Building,
  activity_experiences: Users,
  equipment_hire: Tent,
  toy_hiring: Gamepad2,
  book_hiring: BookOpen,
  bundle_hiring: Gift,
  maternity_items: Heart,
};

// Service Card Component
function ServiceCard({ listing, onClick, onActionClick }) {
  const currencySymbol = listing.currency === 'LKR' ? 'Rs.' : '£';
  const isSafetyAware = listing.card_type === 'safety_aware';
  const isHire = listing.card_type === 'hire' || listing.card_type === 'subscription';
  const isParty = listing.card_type === 'party' || listing.card_type === 'venue';
  
  // Price display format
  const priceDisplay = () => {
    const price = `${currencySymbol}${listing.price}`;
    switch (listing.price_type) {
      case 'per_week': return `${price}/week`;
      case 'per_month': return `${price}/month`;
      case 'hourly': return `${price}/hr`;
      default: return `From ${price}`;
    }
  };
  
  // CTA text based on card type
  const ctaText = () => {
    if (isSafetyAware) return 'Get Help';
    if (isHire) return listing.card_type === 'subscription' ? 'Subscribe' : 'Hire Now';
    if (isParty) return listing.card_type === 'venue' ? 'Book Slot' : 'Check Availability';
    return 'Book';
  };
  
  // Handle button click
  const handleButtonClick = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onActionClick) {
      onActionClick(listing);
    }
  };
  
  return (
    <Card 
      className={`hover:shadow-lg transition-all cursor-pointer group ${
        isSafetyAware ? 'border-l-4 border-l-amber-500' : 
        isHire ? 'border-l-4 border-l-purple-500' :
        isParty ? 'border-l-4 border-l-pink-500' :
        'border-l-4 border-l-blue-500'
      }`}
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
    >
      <CardContent className="p-4">
        {/* Header with category label */}
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">
            {listing.subcategory_name}
          </Badge>
          {isSafetyAware && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              <Shield className="w-3 h-3 mr-1" /> Safety-Aware
            </Badge>
          )}
          {listing.card_type === 'subscription' && (
            <Badge className="text-xs bg-purple-100 text-purple-700">
              Subscription
            </Badge>
          )}
        </div>
        
        {/* Image */}
        {listing.images?.[0] && (
          <div className="relative mb-3 rounded-lg overflow-hidden">
            <img 
              src={listing.images[0]}
              alt={listing.title}
              className="w-full h-40 object-cover group-hover:scale-105 transition-transform"
            />
          </div>
        )}
        
        {/* Title & Description */}
        <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-1">
          {listing.title}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {listing.description}
        </p>
        
        {/* Disclaimer for safety-aware */}
        {isSafetyAware && listing.disclaimer && (
          <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{listing.disclaimer}</span>
          </div>
        )}
        
        {/* Badges */}
        {listing.badges?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {listing.badges.slice(0, 3).map(badgeKey => (
              <BabyKidsTrustBadge key={badgeKey} badgeKey={badgeKey} size="sm" />
            ))}
          </div>
        )}
        
        {/* Subscription features */}
        {listing.card_type === 'subscription' && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1 text-xs text-green-600">
              <span>✓</span> Sanitised before delivery
            </div>
            <div className="flex items-center gap-1 text-xs text-green-600">
              <span>✓</span> Cancel anytime
            </div>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-2">
            {listing.rating > 0 && (
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                <span className="font-medium">{listing.rating.toFixed(1)}</span>
                {listing.review_count > 0 && (
                  <span className="text-xs text-gray-500">({listing.review_count})</span>
                )}
              </div>
            )}
          </div>
          
          <div className="text-right">
            <span className="text-lg font-bold text-gray-900">
              {priceDisplay()}
            </span>
          </div>
        </div>
        
        {/* CTA Button */}
        <Button 
          className={`w-full mt-3 ${
            isSafetyAware ? 'bg-amber-600 hover:bg-amber-700' :
            isHire ? 'bg-purple-600 hover:bg-purple-700' :
            isParty ? 'bg-pink-600 hover:bg-pink-700' :
            ''
          }`}
          size="sm"
          onClick={handleButtonClick}
          data-testid={`action-btn-${listing.id}`}
        >
          {ctaText()}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}

// Subcategory Card Component
function SubcategoryCard({ category, onClick }) {
  const IconComponent = SUBCATEGORY_ICONS[category.id] || ShoppingBag;
  
  return (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`subcategory-${category.id}`}
    >
      <CardContent className="p-4 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
          <IconComponent className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="font-medium text-gray-900">{category.name}</h3>
        <p className="text-xs text-gray-500 mt-1">{category.services_count} services</p>
        {category.sensitive && (
          <Badge variant="outline" className="mt-2 text-xs bg-amber-50 text-amber-700">
            Safety-Aware
          </Badge>
        )}
        {category.subscription_available && (
          <Badge variant="outline" className="mt-2 text-xs bg-purple-50 text-purple-700">
            Subscription
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}

// Main Component
export default function BabyKidsServicesDisplay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filters
  const [country, setCountry] = useState(searchParams.get('country') || 'UK');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/categories?country=${country}`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [country]);
  
  // Fetch listings
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('country', country);
      params.append('sort_by', sortBy);
      params.append('limit', '24');
      
      if (selectedSubcategory) {
        params.append('subcategory', selectedSubcategory);
      }
      
      const response = await fetch(`${API_URL}/api/baby-kids/listings?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setListings(data.listings || []);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [country, selectedSubcategory, sortBy]);
  
  useEffect(() => {
    fetchCategories();
    fetchListings();
  }, [fetchCategories, fetchListings]);
  
  // Filter listings by search
  const filteredListings = listings.filter(listing => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      listing.title?.toLowerCase().includes(query) ||
      listing.description?.toLowerCase().includes(query) ||
      listing.subcategory_name?.toLowerCase().includes(query)
    );
  });
  
  // Filter by tab
  const getFilteredByTab = () => {
    if (activeTab === 'all') return filteredListings;
    if (activeTab === 'hire') return filteredListings.filter(l => ['hire', 'subscription'].includes(l.card_type));
    if (activeTab === 'party') return filteredListings.filter(l => ['party', 'venue'].includes(l.card_type));
    if (activeTab === 'safety') return filteredListings.filter(l => l.card_type === 'safety_aware');
    return filteredListings;
  };
  
  const displayListings = getFilteredByTab();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Baby className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Baby & Kids Items</h1>
          </div>
          <p className="text-blue-100">
            Baby gear, toys, clothing, party services, and more for families in {country === 'UK' ? 'United Kingdom' : 'Sri Lanka'}
          </p>
          
          {/* Search & Filters */}
          <div className="mt-6 flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search services..."
                className="pl-10 h-12 bg-white text-gray-900"
                data-testid="search-input"
              />
            </div>
            
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-40 h-12 bg-white text-gray-900" data-testid="country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">🇬🇧 UK</SelectItem>
                <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 h-12 bg-white text-gray-900" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Subcategory Chips */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
          <Button 
            variant={!selectedSubcategory ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedSubcategory('')}
          >
            All Categories
          </Button>
          {categories.slice(0, 10).map(cat => (
            <Button 
              key={cat.id}
              variant={selectedSubcategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedSubcategory(cat.id)}
              className="whitespace-nowrap"
            >
              {cat.icon} {cat.name}
            </Button>
          ))}
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All Services</TabsTrigger>
            <TabsTrigger value="hire" className="flex items-center gap-1">
              <Gift className="w-4 h-4" /> Hire & Subscribe
            </TabsTrigger>
            <TabsTrigger value="party" className="flex items-center gap-1">
              <PartyPopper className="w-4 h-4" /> Party Services
            </TabsTrigger>
            <TabsTrigger value="safety" className="flex items-center gap-1">
              <Shield className="w-4 h-4" /> Safety-Aware
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Results Count */}
        <p className="text-gray-600 mb-4">
          {loading ? 'Loading...' : `${displayListings.length} services found`}
        </p>
        
        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-40 bg-gray-200 rounded-lg mb-3" />
                  <div className="h-5 w-3/4 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-full bg-gray-100 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : displayListings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters or search term</p>
              <Button variant="outline" onClick={() => {
                setSearchQuery('');
                setSelectedSubcategory('');
                setActiveTab('all');
              }}>
                Clear all filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayListings.map(listing => (
              <ServiceCard 
                key={listing.id}
                listing={listing}
                onClick={() => navigate(`/baby-kids/booking/${listing.id}`)}
                onActionClick={(listing) => navigate(`/baby-kids/booking/${listing.id}`)}
              />
            ))}
          </div>
        )}
        
        {/* Subscription Plans CTA */}
        {activeTab === 'hire' && (
          <Card className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-purple-900">Monthly Toy & Book Subscriptions</h3>
                  <p className="text-purple-700 mt-1">
                    Rotating toys and books delivered to your door. Sanitised and age-appropriate.
                  </p>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  View Plans
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Categories Grid (when no subcategory selected) */}
        {!selectedSubcategory && activeTab === 'all' && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Browse by Category</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {categories.map(cat => (
                <SubcategoryCard 
                  key={cat.id}
                  category={cat}
                  onClick={() => setSelectedSubcategory(cat.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
